import { Injectable, inject } from '@angular/core';
import {
  Firestore,
  collection,
  doc,
  getDoc,
  setDoc,
  updateDoc,
  query,
  where,
  getDocs,
  serverTimestamp,
  addDoc,
  orderBy
} from '@angular/fire/firestore';
import { Scheme } from '../models/user.model';
import { AuthService } from './auth.service';
import { ApprovalService } from './approval.service';

@Injectable({
  providedIn: 'root'
})
export class SchemeService {
  private firestore = inject(Firestore);
  private authService = inject(AuthService);
  private approvalService = inject(ApprovalService);
  private schemesCollection = collection(this.firestore, 'schemes');

  async getSchemesByDept(deptId: string): Promise<Scheme[]> {
    const q = query(this.schemesCollection, where('deptId', '==', deptId), orderBy('name', 'asc'));
    const snapshot = await getDocs(q);
    return snapshot.docs.map(d => ({ id: d.id, ...d.data() } as Scheme));
  }

  async createScheme(scheme: Omit<Scheme, 'id' | 'createdAt' | 'status' | 'createdBy'>): Promise<void> {
    const user = this.authService.currentUser();
    const schemeData = {
      ...scheme,
      status: 'PENDING',
      createdBy: user?.uid || 'unknown',
      createdAt: new Date().toISOString()
    };
    
    await this.approvalService.enqueueChange({
      targetCollection: 'schemes',
      operation: 'create',
      payload: schemeData,
      remarks: 'New scheme creation request'
    });
  }

  async updateScheme(id: string, updates: Partial<Scheme>): Promise<void> {
    await this.approvalService.enqueueChange({
      targetCollection: 'schemes',
      targetDocId: id,
      operation: 'update',
      payload: updates,
      remarks: 'Scheme update request'
    });
  }

  async getMySchemes(): Promise<Scheme[]> {
    const user = this.authService.currentUser();
    if (!user) return [];
    const q = query(this.schemesCollection, where('createdBy', '==', user.uid), orderBy('createdAt', 'desc'));
    const snapshot = await getDocs(q);
    return snapshot.docs.map(d => ({ id: d.id, ...d.data() } as Scheme));
  }
}
